import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useSession } from '../../shell/session-context';
import { useResource } from '../../shell/use-resource';
import { useToast } from '../../shell/toast';
import { Skeleton } from '../../shell/skeleton';
import { ErrorState } from '../../shell/state-view';
import { surfaceOrigin } from '../../shell/surface-links';
import { HomeHero, HomeHeroEmpty } from './home-hero';
import { HomeAttention } from './home-attention';
import { HomeNextUp } from './home-next-up';
import { HomeOffers } from './home-offers';
import { HomeQrOverlay } from './home-qr-overlay';
import { HomeIcon } from './home-icons';
import { OFFERS_SHOWN, attentionRows, liveBookings, todayCount } from './home-model';
import type {
  Booking,
  BookingList,
  OfferCard,
  OfferList,
  Review,
  ReviewList,
  Template,
  TemplateList,
} from '../../mock/seeds/schema-types';
import './home.css';

// Home (PRD v2.5 §7.3.2 route 1; customer gap §A.3 H1–H7; Home.dc.html L41-246). Four
// blocks at most, each present only when useful: the next-booking hero (or the empty
// hero), "Something needs you", "Next up", and the offers marquee — under a greeting
// with one plain status line and the "New booking" pill.
// Reads (all through api.ts): the caller's bookings (GET /me/bookings, every page), the
// caller's reviews (GET /me/reviews — which completed visits still need a rating), the
// template registry (the hero's at-a-glance facts, API-212), and a page of the newest
// offers around the customer's home city (GET /offers). The notifications page is the
// top-bar tray (DR-38); saved offers, deal alerts and the waitlist are deferred (DR-42),
// so the design's waitlist attention row is not built.
const PAGE_LIMIT = 50;
const MAX_PAGES = 10;

interface HomeData {
  bookings: Booking[];
  reviews: Review[];
  templates: Template[];
  offers: OfferCard[];
}

async function loadBookings(signal: AbortSignal): Promise<Booking[]> {
  const out: Booking[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = (await api.GET('/me/bookings', {
      signal,
      params: { query: { limit: PAGE_LIMIT, ...(cursor ? { cursor } : {}) } },
    })) as BookingList;
    out.push(...res.items);
    if (!res.next_cursor) break;
    cursor = res.next_cursor;
  }
  return out;
}

// The secondary reads degrade to "nothing to show" — Home still answers "what is next".
function orEmpty<T>(p: Promise<T>, empty: T): Promise<T> {
  return p.catch(() => empty);
}

export function HomeScreen() {
  const { t, lang } = useLanguage();
  const { user } = useSession();
  const toast = useToast();
  const now = useMemo(() => new Date(), []);
  const [qrOpen, setQrOpen] = useState(false);
  const location = user?.default_location_id;

  const { data, loading, error, reload } = useResource<HomeData>(
    async (signal) => {
      const [bookings, reviews, templates, offers] = await Promise.all([
        loadBookings(signal),
        orEmpty(
          (
            api.GET('/me/reviews', {
              signal,
              params: { query: { limit: 100 } },
            }) as Promise<ReviewList>
          ).then((r) => r.items),
          [] as Review[],
        ),
        orEmpty(
          (api.GET('/registries/templates', { signal, lang }) as Promise<TemplateList>).then(
            (r) => r.items,
          ),
          [] as Template[],
        ),
        orEmpty(
          (
            api.GET('/offers', {
              signal,
              lang,
              params: {
                query: { limit: OFFERS_SHOWN, sort: 'newest', ...(location ? { location } : {}) },
              },
            }) as Promise<OfferList>
          ).then((r) => r.items),
          [] as OfferCard[],
        ),
      ]);
      return { bookings, reviews, templates, offers };
    },
    [lang, location],
  );

  const bookings = data?.bookings ?? [];
  const live = useMemo(() => liveBookings(bookings, now), [bookings, now]);
  const hero = live[0] ?? null;
  const heroCode = hero?.code ?? null;

  // The QR is a server-rendered SVG fetched with auth, shown as a data URI — an <img
  // src> cannot carry the bearer (API-43). Fetched when the overlay opens.
  const { data: qrSvg } = useResource<string | null>(
    (signal) =>
      qrOpen && heroCode
        ? (api.GET('/bookings/{code}/qr', {
            signal,
            params: { path: { code: heroCode } },
          }) as unknown as Promise<string>)
        : Promise.resolve(null),
    [qrOpen, heroCode],
  );

  if (loading && !data) {
    return (
      <div className="hm-screen" aria-busy="true">
        <Skeleton width="40%" height={24} />
        <Skeleton height={420} radius={22} />
        <Skeleton height={160} radius={22} />
      </div>
    );
  }
  if (error || !data) return <ErrorState onRetry={reload} />;

  const website = surfaceOrigin('website') ?? '';
  // New booking / Find a service open the in-app wizard (route 4, DC63), as the design does.
  const findHref = '/bookings/new';
  const today = todayCount(bookings, now);
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';
  const attention = attentionRows(bookings, data.reviews, now);
  const nextUp = live.slice(1, 3);
  const template = hero ? data.templates.find((x) => x.id === hero.template_id) : undefined;
  const qrSrc = qrSvg ? `data:image/svg+xml;utf8,${encodeURIComponent(qrSvg)}` : null;

  const copyCode = () => {
    if (!hero) return;
    void navigator.clipboard?.writeText(hero.code).catch(() => undefined);
    toast.show({ message: t('user.home.hero.copied'), tone: 'ok' });
  };

  return (
    <div className="hm-screen">
      <section className="hm-greet">
        <div className="hm-greet__text">
          <span className="hm-greet__line">
            {formatMessage(lang, 'user.home.greet', { name: firstName })}
          </span>
          <span className="hm-greet__status">
            {today > 0
              ? formatMessage(lang, 'user.home.today_count', { n: today })
              : t('user.home.today_none')}
          </span>
        </div>
        <Link className="hm-pill hm-greet__new" to={findHref} state={{ from: '/' }}>
          <HomeIcon name="plus" />
          {t('user.home.new_booking')}
        </Link>
      </section>

      {hero ? (
        <HomeHero
          booking={hero}
          template={template}
          now={now}
          onCopy={copyCode}
          onQr={() => setQrOpen(true)}
        />
      ) : (
        <HomeHeroEmpty emptyAccount={bookings.length === 0} findHref={findHref} />
      )}

      {attention.length > 0 && <HomeAttention rows={attention} />}
      {nextUp.length > 0 && <HomeNextUp bookings={nextUp} now={now} />}
      {data.offers.length > 0 && <HomeOffers offers={data.offers} websiteOrigin={website} />}

      <HomeQrOverlay
        code={qrOpen ? heroCode : null}
        qrSrc={qrSrc}
        onClose={() => setQrOpen(false)}
      />
    </div>
  );
}
